import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, Edit, Trash2, Store, Save } from "lucide-react";

export default function AdminFoodCourts() {
  const [courts, setCourts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [pickedIds, setPickedIds] = useState(new Set());

  const load = async () => {
    try {
      const [c, v] = await Promise.all([
        api.get("/food-courts"),
        api.get("/vendors"),
      ]);
      setCourts(c.data);
      setVendors(v.data);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { name: editing.name, location: editing.location || "", description: editing.description || "", status: editing.status || "active" };
      if (editing.id) await api.put(`/food-courts/${editing.id}`, payload);
      else await api.post("/food-courts", payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const remove = async (fc) => {
    if (!window.confirm(`Delete "${fc.name}"? Counters will be detached.`)) return;
    try {
      await api.delete(`/food-courts/${fc.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const openAssign = (fc) => {
    const ids = new Set(vendors.filter(v => v.food_court_id === fc.id).map(v => v.id));
    setPickedIds(ids);
    setAssigning(fc);
  };

  const toggleVendor = (vid) => {
    setPickedIds(prev => {
      const next = new Set(prev);
      if (next.has(vid)) next.delete(vid); else next.add(vid);
      return next;
    });
  };

  const saveAssign = async () => {
    try {
      await api.post(`/food-courts/${assigning.id}/assign-vendors`, { vendor_ids: Array.from(pickedIds) });
      toast.success("Counters mapped");
      setAssigning(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const ungrouped = vendors.filter(v => !v.food_court_id);

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Food Courts</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Courts &amp; counters</h1>
            <div className="text-sm text-ink-600 mt-1">Group counters into food courts (e.g., Main Court, Block B Cafe). Counters without a court behave like a single-canteen.</div>
          </div>
          <button data-testid="new-court-btn" onClick={() => setEditing({ name: "", location: "", description: "", status: "active" })}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold">
            <Plus size={16} /> New food court
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-8">
          {courts.map(fc => {
            const counters = vendors.filter(v => v.food_court_id === fc.id);
            return (
              <div key={fc.id} className="bg-white rounded-3xl border border-border p-6" data-testid={`court-card-${fc.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-cream-200 flex items-center justify-center text-brand">
                      <Building2 size={22} />
                    </div>
                    <div>
                      <div className="font-heading text-xl font-bold text-ink">{fc.name}</div>
                      <div className="text-xs text-ink-500">{fc.location || "—"}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${fc.status === 'active' ? 'bg-emerald-100 text-emerald-900' : 'bg-cream-300 text-ink-700'}`}>{fc.status}</span>
                </div>
                {fc.description && <div className="text-sm text-ink-600 mt-3">{fc.description}</div>}
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">Counters ({counters.length})</div>
                  {counters.length === 0 ? (
                    <div className="text-xs text-ink-500 italic">No counters mapped yet</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {counters.map(c => (
                        <span key={c.id} data-testid={`court-counter-${c.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cream-200 text-xs font-bold text-ink">
                          <Store size={10} /> {c.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button data-testid={`court-assign-${fc.id}`} onClick={() => openAssign(fc)} className="px-3 py-1.5 rounded-full bg-ink text-white text-xs font-semibold flex items-center gap-1"><Store size={12} /> Map counters</button>
                  <button data-testid={`court-edit-${fc.id}`} onClick={() => setEditing(fc)} className="px-3 py-1.5 rounded-full bg-cream-200 text-xs font-semibold flex items-center gap-1"><Edit size={12} /> Edit</button>
                  <button data-testid={`court-delete-${fc.id}`} onClick={() => remove(fc)} className="px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            );
          })}
          {courts.length === 0 && (
            <div className="col-span-full bg-cream-100 rounded-3xl border border-dashed border-border p-10 text-center">
              <Building2 size={32} className="mx-auto text-ink-400" />
              <div className="font-heading text-xl font-bold mt-3">No food courts yet</div>
              <div className="text-sm text-ink-600 mt-1">Add a food court to group counters by location.</div>
            </div>
          )}
        </div>

        {ungrouped.length > 0 && (
          <div className="bg-white rounded-3xl border border-border p-6">
            <div className="font-heading text-xl font-bold">Ungrouped counters</div>
            <div className="text-sm text-ink-600 mt-1">These vendors aren't mapped to any food court yet.</div>
            <div className="flex flex-wrap gap-2 mt-4">
              {ungrouped.map(v => (
                <span key={v.id} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-cream-200 text-sm font-bold text-ink">
                  <Store size={12} /> {v.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="font-heading text-2xl font-bold">{editing.id ? "Edit" : "New"} food court</div>
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Name *</label>
                <input data-testid="court-form-name" value={editing.name ?? ""} onChange={e => setEditing({ ...editing, name: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Location</label>
                <input data-testid="court-form-location" value={editing.location ?? ""} onChange={e => setEditing({ ...editing, location: e.target.value })}
                  placeholder="e.g., Block B, Ground Floor"
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Description</label>
                <textarea data-testid="court-form-description" value={editing.description ?? ""} onChange={e => setEditing({ ...editing, description: e.target.value })}
                  rows={3}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Status</label>
                <select data-testid="court-form-status" value={editing.status ?? "active"} onChange={e => setEditing({ ...editing, status: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
              <button data-testid="court-save" onClick={save} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}

      {assigning && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setAssigning(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="font-heading text-2xl font-bold">Map counters → {assigning.name}</div>
            <div className="text-sm text-ink-600 mt-1">Pick the counters that belong to this food court. A counter can only belong to one court at a time.</div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {vendors.map(v => {
                const picked = pickedIds.has(v.id);
                const otherCourt = v.food_court_id && v.food_court_id !== assigning.id;
                return (
                  <label key={v.id} data-testid={`assign-vendor-${v.id}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${picked ? 'bg-brand/5 border-brand' : 'bg-white border-border hover:bg-cream-100'}`}>
                    <input type="checkbox" checked={picked} onChange={() => toggleVendor(v.id)} className="w-4 h-4 accent-brand" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm text-ink truncate">{v.name}</div>
                      <div className="text-[11px] text-ink-500">{v.category || "—"} · {v.operator_type === "school" ? "School-operated" : "Vendor-operated"}</div>
                      {otherCourt && !picked && <div className="text-[10px] text-amber-700 mt-0.5">Currently in another court — selecting will move it here.</div>}
                    </div>
                  </label>
                );
              })}
              {vendors.length === 0 && <div className="col-span-full text-center text-ink-500 py-6 text-sm">No counters configured yet. Create vendors first.</div>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setAssigning(null)} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
              <button data-testid="assign-save" onClick={saveAssign} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold inline-flex items-center gap-2"><Save size={14} /> Save mapping</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
